import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import classes from './Expense.module.scss';
import { useBlocker, useWindowSize, usePrevious } from '../../../utils/hooks';
import { responsiveSizeTimesheet, timesheetsStatuses } from '../../../utils/constants';
import NavigationComponent from '../../common/NavigationComponent/NavigationComponent';
// eslint-disable-next-line import/no-cycle
import ExpenseHeader from './components/ExpenseHeader/ExpenseHeader';
// eslint-disable-next-line import/no-cycle
import MainTable from './components/MainTable/MainTable';
import { tneAxiosClient } from '../../../api/apiSettings';
import { generalEnums } from '../../../utils/enums';
import TimeandExpensePopup from '../../common/ModalWindows/TimeandExpensePopup/TimeandExpensePopup';
import BaseModal from '../../common/ModalWindows/BaseModal';
import WithCloseIcon from '../../common/ComposableWrappers/WithCloseIcon/WithCloseIcon';
import NotificationModal from '../../common/ModalWindows/NotificationModal';
import { getListOfApprovers } from '../../../actions/user';
import SubmitModal from '../timesheet/SubmitModal/SubmitModal';
import {
  addNewClaim,
  changeExpenseStatus,
  deleteClaimLineById,
  resetNewRow,
  saveExpenseComment,
  updateClaimTable,
  updateExpenseComment
} from '../../../actions/expense';
import { basicErrorHandler } from '../../../utils/helpers';
import { DeletePopUp, UnsavedChanges } from '../timesheet/components/modals/modals';
import ApproverComment from '../timesheet/ApproverComment/ApproverComment';
import { PageContext } from '../../../context/context';
// eslint-disable-next-line import/no-cycle
import TimeandExpenseActions from '../../common/buttons/TimeandExpenseActions/TimeandExpenseActions';
import { FinalModal, RejectModal } from './components/Modals/Modals';
import MobileHeadMenu from '../timesheet/components/MobileHeadMenu/MobileHeadMenu';
import { deleteClaim } from '../../../actions/expenses';

export const EditExpenseContext = createContext(null);

export default function Expense() {
  const size = useWindowSize();
  const params = useParams();
  const expense = useSelector((state) => state.expense.data);
  const approvers = useSelector((state) => state.user.approvers);
  const commentsaving = useSelector((state) => state.expense.commentPending);
  const titles = useSelector((state) => state.constFromApi.placements);
  const [isEdit, setIsEdit] = useState(size.width < responsiveSizeTimesheet);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const [hasChanges, setHasChanges] = useState(false);
  const [openClaimSwiper, setOpenClaimSwiper] = useState(false);
  const [lineToDelete, setLineToDelete] = useState('');
  const [saving, setSavaing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [openPopUp, setOpenPop] = useState('');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ctx = useContext(PageContext);
  const [searchParams] = useSearchParams();
  const locationQueryParams = useLocation();
  const deletePending = useSelector((state) => state.expenses.pendingDelete);
  const prevPendingDelete = usePrevious(deletePending);

  useBlocker(() => setOpenPop('unsavedChanges'), hasChanges);

  const getRequestFormData = (data) => {
    const formData = new FormData();
    const payCurrency = titles?.find((item) => item.PoNumber === expense?.PoNumber)?.PayCurrency;
    data?.ExpenseDetails?.forEach((detail, index) => {
      const formDetail = Object.keys(detail);
      formDetail.map((label) => {
        detail['Date'] = dayjs(detail['Date']).format(generalEnums.DATEFORMAT);
        if (!detail['FxCurrencyId'] || !detail['FxAmount']) {
          detail['FxCurrencyId'] = detail['ExpenseCurrencyId'];
          detail['FxAmount'] = 1;
        }
        formData.append(`ExpenseDetails[${index}].${label}`, detail[label]);
        formData.delete(`ExpenseDetails[${index}].Category`);
        formData.delete(`ExpenseDetails[${index}].TotalAmount`);
        formData.delete(`ExpenseDetails[${index}].ExpenseAttachments`);
        formData.delete(`ExpenseDetails[${index}].attachments`);
        formData.delete(`ExpenseDetails[${index}].ExpenseCurrency`);
        formData.delete(`ExpenseDetails[${index}].FxCurrency`);
        if (detail.Type === 'New') {
          formData.delete(`ExpenseDetails[${index}].Id`);
          formData.delete(`ExpenseDetails[${index}].Type`);
        }
        if (detail[label]?.length === 0) {
          formData.delete(`ExpenseDetails[${index}].${label}`);
        }
        if (detail['ExpenseCurrency'] === payCurrency) {
          formData.delete(`ExpenseDetails[${index}].FxAmount`);
          formData.delete(`ExpenseDetails[${index}].FxCurrency`);
        }

        detail?.attachments?.map((attachment) => {
          return formData.append(`ExpenseDetails[${index}].attachments`, attachment);
        });
        return formData;
      });
    });

    return formData;
  };

  const saveHandler = async () => {
    setSavaing(true);
    try {
      setHasChanges(false);
      const formData = getRequestFormData(expense);
      const response = await tneAxiosClient.put(`v1/expense?expenseId=${expense.Id}`, formData);
      setOpenClaimSwiper(false);
      if (response.status === 200) {
        const updatedExpenseDetails = response?.data?.Data?.ExpenseDetails.filter((item) => {
          if (item.ExpenseAttachments.length === 0) {
            item.NoReceiptCheck = true;
          }
          return item;
        });
        dispatch(updateClaimTable(response?.data?.Data, { ExpenseDetails: updatedExpenseDetails }));
        dispatch(changeExpenseStatus(timesheetsStatuses(t).draft.apiStatus));
        dispatch(resetNewRow());
        setOpenPop('saved');
      }
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setSavaing(false);
  };

  const submitHandler = () => {
    if (
      expense.ExpenseDetails.some(
        (item) => item && item?.NoReceiptCheck === false && item.ExpenseAttachments?.length === 0
      )
    ) {
      setError(t('dashboard.expenses.validations.attachment'));
      return;
    }

    if (!expense.ExpenseDetails.every((item) => item.Comment)) {
      setError(t('dashboard.expenses.validations.comment'));
      return;
    }

    if (!expense.ExpenseDetails.every((item) => item?.ExpenseAmount !== 0)) {
      setError(t('dashboard.expenses.validations.expenseamount'));
      return;
    }

    setError('');
    dispatch(getListOfApprovers(expense.ClientCompanyId));

    setOpenPop('submit');
  };

  const totalClaim = useMemo(() => {
    return expense?.ExpenseDetails?.reduce((acc, obj) => {
      return parseFloat(acc) + parseFloat(obj.TotalAmount);
    }, 0);
  }, [expense?.ExpenseDetails]);

  const expenseCurrency = useMemo(() => {
    return expense?.ExpenseDetails[0]?.ExpenseCurrency?.Symbol;
  }, [expense?.ExpenseDetails]);

  const finalSubmitClaim = async (approver, cb) => {
    try {
      const formData = getRequestFormData(expense);
      formData.append('ExpenseStatus', timesheetsStatuses(t).submitted.apiStatus);
      formData.append('ApproverId', approver);
      await tneAxiosClient.put(`v1/expense?expenseId=${expense.Id}`, formData);
      setHasChanges(false);
      dispatch(changeExpenseStatus(timesheetsStatuses(t).submitted.apiStatus));
      setIsEdit(false);
      cb();
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
  };

  const finalApproveExpense = async () => {
    try {
      setApproving(true);
      await tneAxiosClient.post(`v1/expense-action-by-approver`, {
        ExpenseId: expense.Id,
        Status: timesheetsStatuses(t).approved.apiStatus
      });
      dispatch(changeExpenseStatus(timesheetsStatuses(t).approved.apiStatus));
      setOpenPop('approved');
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setApproving(false);
  };

  const finalRejectExpense = async () => {
    try {
      setRejecting(true);
      await tneAxiosClient.post(`v1/expense-action-by-approver`, {
        ExpenseId: expense.Id,
        Status: timesheetsStatuses(t).rejected.apiStatus
      });
      dispatch(changeExpenseStatus(timesheetsStatuses(t).rejected.apiStatus));
      setOpenPop('rejected');
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setRejecting(false);
  };

  const deleteLine = () => {
    if (expense.ExpenseDetails.length === 1) {
      dispatch(addNewClaim());
    }
    dispatch(deleteClaimLineById(lineToDelete));
    setHasChanges(true);
    setOpenPop('');
    setLineToDelete('');
  };

  const saveCommentHandler = (comment, commentId = '') => {
    if (commentId) {
      dispatch(updateExpenseComment(comment, commentId));
    } else {
      dispatch(saveExpenseComment(comment, expense.Id));
    }
  };

  const deleteHandler = async () => {
    setOpenPop('deleteExpense');
  };

  const finalDeleteExpenseHandler = () => {
    dispatch(deleteClaim(expense?.Id));
  };

  const popUpModals = {
    saved: (
      <TimeandExpensePopup
        title={t('dashboard.expenses.table.saved')}
        primaryButton={{
          text: t('dashboard.expenses.newbuttontxt'),
          action: () => navigate('/dashboard/create-claim')
        }}
        secondaryButton={{
          text: t('common.general.gotodashboard'),
          action: () => navigate('/dashboard/expenses')
        }}
      />
    ),
    unsavedChanges: (
      <UnsavedChanges
        saveHandler={saveHandler}
        closeHandler={() => setOpenPop(false)}
        rejectPreventing={setHasChanges}
      />
    ),
    delete: (
      <DeletePopUp
        deleteHandler={() => deleteLine(lineToDelete)}
        closeHandler={() => {
          setOpenPop(false);
          setLineToDelete('');
        }}
      />
    ),
    deleteExpense: (
      <DeletePopUp
        deleteHandler={finalDeleteExpenseHandler}
        closeHandler={() => {
          setOpenPop(false);
        }}
        pending={deletePending}
      />
    ),
    submit: <SubmitModal submitHandler={finalSubmitClaim} approvers={approvers} Id={expense?.Id} />,
    approved: (
      <FinalModal
        message={
          ctx.pageType === generalEnums.TIMESHEET
            ? t('dashboard.timesheet.approvedsuccessfully')
            : t('dashboard.expenses.approvedsuccessfully')
        }
        isTimesheet={false}
        queryParams={locationQueryParams.search}
      />
    ),
    approve: (
      <TimeandExpensePopup
        title={t('dashboard.timesheet.approveconfirmation')}
        primaryButton={{
          text: t('common.general.approve'),
          action: finalApproveExpense
        }}
        pending={approving}
      />
    ),
    rejected: (
      <FinalModal
        message={
          ctx.pageType === generalEnums.TIMESHEET
            ? t('dashboard.timesheet.rejectedsuccessfully')
            : t('dashboard.expenses.rejectedsuccessfully')
        }
        isTimesheet={false}
      />
    ),
    reject: <RejectModal />
  };

  const rejectHandler = () => {
    if (expense?.ExpenseRejectionReasons.every((comment) => comment.ActionTaken !== 'Pending')) {
      setOpenPop('reject');
      return;
    }
    finalRejectExpense();
  };

  const approveHandler = () => {
    setOpenPop('approve');
  };

  function alertUser(event) {
    if (!hasChanges) return;
    event.preventDefault();
    // eslint-disable-next-line no-return-assign,consistent-return
    return (event.returnValue = '');
  }

  useEffect(() => {
    if (prevPendingDelete && !deletePending) {
      navigate('/dashboard/expenses', { state: { idToDelete: expense.Id } });
    }
  }, [prevPendingDelete, deletePending]);

  useEffect(() => {
    const id = setInterval(() => {
      if (isEdit && hasChanges) {
        saveHandler(false);
      }
    }, 120000);
    return () => clearInterval(id);
  }, [isEdit, hasChanges]);

  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, [hasChanges]);

  useEffect(() => {
    return () => {
      if (hasChanges) setOpenPop('unsavedChanges');
    };
  }, []);

  useEffect(() => {
    if (lineToDelete) setOpenPop('delete');
  }, [lineToDelete]);

  useEffect(() => {
    ctx.setPageType(generalEnums.EXPENSE);
  }, [ctx]);

  useEffect(() => {
    if (!isApprover && size.width < responsiveSizeTimesheet) setIsEdit(size.width < responsiveSizeTimesheet);
  }, [size.width]);

  useEffect(() => {
    const approverModal = searchParams.get('approver');
    const isOnlySubmitted = expense.Status.Status === timesheetsStatuses(t).submitted.apiStatus;
    if (approverModal === 'true' && isOnlySubmitted) {
      approveHandler();
    }
  }, [searchParams]);

  useEffect(() => {
    const editMode = searchParams.get('editMode');

    if (editMode === 'true' && expense?.Status?.Status === timesheetsStatuses(t).draft.apiStatus) {
      setIsEdit(true);
    }
  }, [searchParams]);

  const contextValue = useMemo(
    () => ({ isEdit, setIsEdit, expense, hasChanges, setHasChanges, openClaimSwiper, setOpenClaimSwiper }),
    [isEdit, setIsEdit, expense, hasChanges, setHasChanges, openClaimSwiper, setOpenClaimSwiper]
  );

  return (
    <EditExpenseContext.Provider value={contextValue}>
      <section className={classes.mainContainer}>
        {!(size.width > responsiveSizeTimesheet) ? (
          <NavigationComponent label="" linkBack={`/dashboard/expenses${locationQueryParams.search}`}>
            <MobileHeadMenu
              isApprover={isApprover}
              data={expense}
              pageType={generalEnums.EXPENSE}
              deleteHandler={deleteHandler}
            />
          </NavigationComponent>
        ) : (
          <div className={classes.closeContainer}>
            <button onClick={() => navigate(`/dashboard/expenses${locationQueryParams.search}`)}>
              <CloseIcon />
            </button>
          </div>
        )}
        <ExpenseHeader
          error={error}
          expense={expense}
          save={saveHandler}
          submit={submitHandler}
          approve={approveHandler}
          reject={rejectHandler}
          saving={saving}
          rejecting={rejecting}
          isApprover={isApprover}
          getFormData={(data) => getRequestFormData(data)}
        />
        <MainTable
          expenseCurrency={expenseCurrency}
          totalClaim={totalClaim}
          data={expense || []}
          save={saveHandler}
          submit={submitHandler}
          deleteLine={setLineToDelete}
          approve={approveHandler}
          reject={rejectHandler}
          saving={saving}
          isApprover={isApprover}
          getFormData={(data) => getRequestFormData(data)}
        />
        {(expense?.Status.Status === timesheetsStatuses(t).rejected.apiStatus ||
          (isApprover && expense?.Status.Status === timesheetsStatuses(t).submitted.apiStatus)) && (
          <ApproverComment
            comments={expense?.ExpenseRejectionReasons?.sort((a, b) => (a.Id > b.Id ? -1 : 1)) || []}
            disabled={
              !isApprover ||
              [
                timesheetsStatuses(t).approved.apiStatus,
                timesheetsStatuses(t).processed.apiStatus,
                timesheetsStatuses(t).rejected.apiStatus
              ].includes(expense.Status.Status)
            }
            isApprover={isApprover}
            extraClass={classes.commentSection}
            saveComment={saveCommentHandler}
            commentSaving={commentsaving}
          />
        )}
        {!(size.width > responsiveSizeTimesheet) && (
          <div className={classes.actions}>
            <TimeandExpenseActions
              error={error}
              saving={saving}
              rejecting={rejecting}
              save={saveHandler}
              submit={submitHandler}
              approve={approveHandler}
              reject={rejectHandler}
              isApprover={isApprover}
              status={expense?.Status?.Status}
            />
          </div>
        )}
        <BaseModal
          open={openPopUp}
          fullscreen={false}
          closeHandler={() => setOpenPop('')}
          title="&nbsp;"
          hideFooter
          hideHeader
          withSeparator={false}
          marginLeftRight={1}
        >
          <WithCloseIcon close={() => setOpenPop('')}>{popUpModals[openPopUp]}</WithCloseIcon>
        </BaseModal>
        {error && (
          <NotificationModal open={error} onClose={() => setError('')}>
            <p className={classes.errorMessage}>{error}</p>
          </NotificationModal>
        )}
      </section>
    </EditExpenseContext.Provider>
  );
}
